import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminAffiliatesApi,
  AdminApiError,
  type AdminAttributionRequest,
  type AdminRiskFlag,
} from '../../api/admin.ts';
import { Alert, Button, LoadingBlock, Select, TextInput } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { Pill, TableShell, Td, Th, money, partnerStatusTone, riskTone, shortDate } from './affiliate-common.tsx';

/**
 * The Affiliate Program's operator home: the money at a glance, the queues
 * that need a decision (applications, attribution claims, risk flags), and
 * the partner roster. Everything that concerns ONE partner — status, ledger,
 * adjustments, notes — lives on that partner's own page; the monthly payout
 * run has its own screen at /admin/partners/payouts.
 */

const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'application_pending', label: 'Application pending' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'payout_hold', label: 'Payout hold' },
  { value: 'compliance_review', label: 'Compliance review' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'terminated', label: 'Terminated' },
];

export default function AdminPartnersPage() {
  useDocumentTitle('Partners');
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ['admin', 'affiliates', 'overview'],
    queryFn: ({ signal }) => adminAffiliatesApi.overview(signal),
  });
  const partners = useQuery({
    queryKey: ['admin', 'affiliates', 'partners', statusFilter, page],
    queryFn: ({ signal }) =>
      adminAffiliatesApi.listPartners({ ...(statusFilter ? { status: statusFilter } : {}), page }, signal),
  });
  // Asked for BY STATUS, one request per open state, rather than fetched
  // whole and filtered here: the API answers the newest rows only, so a run
  // of recently decided claims would otherwise push older still-open ones out
  // of the window — off this queue while the overview still counts them.
  const claims = useQuery({
    queryKey: ['admin', 'affiliates', 'claims', 'open'],
    queryFn: async ({ signal }) => {
      const [pending, conflict] = await Promise.all([
        adminAffiliatesApi.listAttributionRequests('pending', signal),
        adminAffiliatesApi.listAttributionRequests('conflict', signal),
      ]);
      return {
        requests: [...conflict.requests, ...pending.requests],
        // Each status answers its newest 100. A full page means older open
        // claims exist beyond it, and the queue must say so rather than
        // present itself as the whole backlog.
        truncated: pending.requests.length >= 100 || conflict.requests.length >= 100,
      };
    },
  });
  const risks = useQuery({
    queryKey: ['admin', 'affiliates', 'risk'],
    queryFn: ({ signal }) => adminAffiliatesApi.listRiskFlags(signal),
  });

  // Names for the ids the queue rows carry. Filtered lists may not contain
  // every partner a claim names, so this map is best-effort and the id is the
  // fallback.
  const partnerNames = new Map((partners.data?.partners ?? []).map((row) => [row.id, row.displayName]));

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'affiliates'] });
  }

  const openClaims = claims.isSuccess && !claims.isFetching ? claims.data.requests : [];
  const openRisks = risks.isSuccess && !risks.isFetching ? risks.data.flags : [];

  return (
    <div className="portal-page">
      <WorkPage
        title="Partners"
        description="The affiliate program: who promotes the product, which customers they brought, and what that costs."
        actions={
          <Link to="/admin/partners/payouts" className="btn btn-primary">
            Monthly payouts
          </Link>
        }
      >
        {error ? (
          <div className="mb-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        {overview.isSuccess && !overview.isFetching && !overview.data.payoutsConfigured ? (
          <div className="mb-4">
            <Alert tone="warning" title="PayPal payouts are not configured">
              Balances accrue and payouts can be calculated and approved, but nothing can be sent until the PayPal
              payout credentials are set on this deployment.
            </Alert>
          </div>
        ) : null}

        {overview.isError ? (
          <Alert tone="error">
            The program overview could not be loaded.{' '}
            <Button variant="ghost" onClick={() => void overview.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : overview.isFetching ? (
          <LoadingBlock label="Loading program figures…" />
        ) : overview.data ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              label="Active partners"
              value={String(overview.data.partners.active)}
              sub={`${overview.data.partners.pendingApplications} pending · ${overview.data.partners.newThisMonth} new this month`}
            />
            <StatTile
              label="Referred paying customers"
              value={String(overview.data.customers.activePaying)}
              sub={`${money(overview.data.customers.mrrCents)} MRR`}
            />
            <StatTile
              label="Commission accrued"
              value={money(overview.data.money.commissionAccruedCents)}
              sub={`${overview.data.money.blendedCommissionPercent}% of ${money(overview.data.money.eligibleRevenueCents)} eligible revenue`}
            />
            <StatTile
              label="Commission paid"
              value={money(overview.data.money.commissionPaidCents)}
              sub={`${overview.data.queues.openAttributionClaims} open claims · ${overview.data.queues.openRiskFlags} risk flags`}
            />
          </div>
        ) : null}

        {claims.isFetching ? <LoadingBlock label="Loading claims…" /> : null}

        {claims.isError ? (
          <section className="mt-6">
            <h2 className="mb-3 text-base font-semibold">Attribution claims awaiting a decision</h2>
            {/* A failed fetch must not read as an empty queue — the overview
              may be counting open claims this very moment. */}
            <Alert tone="error" title="The claim queue could not be loaded">
              <Button variant="ghost" onClick={() => void claims.refetch()}>
                Try again
              </Button>
            </Alert>
          </section>
        ) : null}

        {openClaims.length > 0 ? (
          <section className="mt-6">
            <h2 className="mb-3 text-base font-semibold">Attribution claims awaiting a decision</h2>
            {claims.data?.truncated ? (
              <p className="mb-3 text-sm text-[color:var(--color-muted)]">
                Showing the newest 100 per status — older open claims surface as these are decided.
              </p>
            ) : null}
            <div className="space-y-3">
              {openClaims.map((request) => (
                <ClaimCard
                  key={request.id}
                  request={request}
                  partnerName={partnerNames.get(request.affiliateId) ?? request.affiliateId}
                  onDone={() => void refresh()}
                  onError={setError}
                />
              ))}
            </div>
          </section>
        ) : null}

        {risks.isError ? (
          <section className="mt-6">
            <h2 className="mb-3 text-base font-semibold">Open risk flags</h2>
            {/* Same rule as the claim queue: a failed fetch must not read as
              "no risk work" while the overview counts open flags. */}
            <Alert tone="error" title="The risk queue could not be loaded">
              <Button variant="ghost" onClick={() => void risks.refetch()}>
                Try again
              </Button>
            </Alert>
          </section>
        ) : null}

        {risks.isFetching ? <LoadingBlock label="Loading risk flags…" /> : null}

        {openRisks.length > 0 ? (
          <section className="mt-6">
            <h2 className="mb-3 text-base font-semibold">Open risk flags</h2>
            {openRisks.length >= 100 ? (
              <p className="mb-3 text-sm text-[color:var(--color-muted)]">
                Showing the newest 100 — older open flags surface as these are resolved. The overview counts them all.
              </p>
            ) : null}
            <div className="space-y-3">
              {openRisks.map((flag) => (
                <RiskCard
                  key={flag.id}
                  flag={flag}
                  partnerName={flag.affiliateId ? (partnerNames.get(flag.affiliateId) ?? flag.affiliateId) : null}
                  onDone={() => void refresh()}
                  onError={setError}
                />
              ))}
            </div>
          </section>
        ) : null}

        <section className="mt-6">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <h2 className="text-base font-semibold">All partners</h2>
            <div className="w-56">
              <Select
                label="Status"
                options={STATUS_FILTERS}
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  // A new filter is a new list; page 3 of the old one is nowhere.
                  setPage(1);
                }}
              />
            </div>
          </div>

          {partners.isLoading ? (
            <LoadingBlock label="Loading partners…" />
          ) : partners.isError ? (
            // A failed fetch, said as one — the empty-roster copy below would
            // read a network failure as "nobody has applied".
            <Alert tone="error" title="The roster could not be loaded">
              <Button variant="ghost" onClick={() => void partners.refetch()}>
                Try again
              </Button>
            </Alert>
          ) : (partners.data?.partners.length ?? 0) === 0 ? (
            <p className="text-sm text-[color:var(--color-muted)]">
              {statusFilter ? 'No partners with that status.' : 'Nobody has applied to the program yet.'}
            </p>
          ) : (
            <TableShell
              label="All partners"
              head={
                <tr>
                  <Th>Partner</Th>
                  <Th>Link</Th>
                  <Th>Status</Th>
                  <Th>Risk</Th>
                  <Th>Rate</Th>
                  <Th>PayPal</Th>
                  <Th>Tax docs</Th>
                  <Th>Joined</Th>
                </tr>
              }
            >
              {(partners.data?.partners ?? []).map((partner) => (
                <tr key={partner.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                  {/* Applicant-controlled: a 200-character display name and a
                    320-character address. The shell sizes to its content, so
                    both are bounded and wrap rather than setting the width of
                    every row in the roster. */}
                  <Td className="table-text max-w-[20rem]">
                    <Link
                      to={`/admin/partners/${partner.id}`}
                      className="font-medium text-[color:var(--color-brand)] hover:underline"
                    >
                      {partner.displayName}
                    </Link>
                    <div className="text-xs text-[color:var(--color-muted)]">{partner.contactEmail}</div>
                  </Td>
                  <Td>
                    <code className="rounded bg-[color:var(--color-surface)] px-1.5 py-0.5">/p/{partner.slug}</code>
                  </Td>
                  <Td>
                    <Pill value={partner.status} tone={partnerStatusTone(partner.status)} />
                  </Td>
                  <Td>
                    <Pill value={partner.riskState} tone={riskTone(partner.riskState)} />
                  </Td>
                  <Td>
                    {partner.negotiatedRatePercent === null ? 'Tier' : `${partner.negotiatedRatePercent}% (negotiated)`}
                  </Td>
                  <Td>{partner.paypalOnFile ? 'On file' : '—'}</Td>
                  <Td>{partner.taxDocumentationOnFile ? 'On file' : '—'}</Td>
                  <Td className="text-[color:var(--color-muted)]">{shortDate(partner.createdAt)}</Td>
                </tr>
              ))}
            </TableShell>
          )}

          {page > 1 || partners.data?.hasMore ? (
            <div className="mt-3 flex items-center justify-between">
              <Button variant="ghost" disabled={page <= 1 || partners.isFetching} onClick={() => setPage(page - 1)}>
                ← Newer
              </Button>
              <span className="text-sm text-[color:var(--color-muted)]">Page {page}</span>
              <Button
                variant="ghost"
                disabled={!partners.data?.hasMore || partners.isFetching}
                onClick={() => setPage(page + 1)}
              >
                Older →
              </Button>
            </div>
          ) : null}
        </section>
      </WorkPage>
    </div>
  );
}

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card">
      <p className="text-sm font-medium text-[color:var(--color-muted)]">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {sub ? <p className="mt-1 text-xs text-[color:var(--color-muted)]">{sub}</p> : null}
    </div>
  );
}

/**
 * One claim, decided in place. Approval attaches the customer to the partner,
 * so it needs the company id in hand — prefilled with the system's own match
 * when there is one — and, like every decision in this console, a reason.
 */
function ClaimCard({
  request,
  partnerName,
  onDone,
  onError,
}: {
  request: AdminAttributionRequest;
  partnerName: string;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [companyId, setCompanyId] = useState(request.matchedCompanyId ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'approved' | 'rejected' | null>(null);

  async function decide(status: 'approved' | 'rejected') {
    setBusy(status);
    try {
      await adminAffiliatesApi.decideAttributionRequest(request.id, {
        status,
        reason: reason.trim(),
        ...(status === 'approved' && companyId.trim() ? { companyId: companyId.trim() } : {}),
      });
      onDone();
    } catch (caught) {
      onError(caught instanceof AdminApiError ? caught.message : 'The claim could not be decided.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{partnerName}</span>
        {/* The email is never hidden behind the business name: it is the
            address the customer signed up with — the piece of evidence an
            operator actually matches a company against. */}
        <span className="text-sm text-[color:var(--color-muted)]">
          claims {request.businessName ? `${request.businessName} · ` : ''}
          {request.customerEmail}
        </span>
        <Pill value={request.status} tone={request.status === 'conflict' ? 'bad' : 'warn'} />
        <span className="ml-auto text-xs text-[color:var(--color-muted)]">{shortDate(request.createdAt)}</span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-sm">{request.explanation}</p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <TextInput
            label="Company id"
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
            hint={
              request.matchedCompanyId ? 'Matched automatically — verify before approving.' : 'Required to approve.'
            }
          />
        </div>
        <div className="min-w-64 flex-1">
          <TextInput
            label="Reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="What you verified, or why this claim fails"
          />
        </div>
        <div className="flex gap-2 pb-4">
          <Button
            variant="primary"
            busy={busy === 'approved'}
            disabled={busy !== null || !reason.trim() || !companyId.trim()}
            onClick={() => void decide('approved')}
          >
            Approve
          </Button>
          <Button
            variant="danger"
            busy={busy === 'rejected'}
            disabled={busy !== null || !reason.trim()}
            onClick={() => void decide('rejected')}
          >
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
}

function RiskCard({
  flag,
  partnerName,
  onDone,
  onError,
}: {
  flag: AdminRiskFlag;
  partnerName: string | null;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function resolve() {
    setBusy(true);
    try {
      await adminAffiliatesApi.resolveRiskFlag(flag.id, reason.trim());
      onDone();
    } catch (caught) {
      onError(caught instanceof AdminApiError ? caught.message : 'The flag could not be resolved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="flex flex-wrap items-center gap-2">
        <Pill value={flag.severity} tone={flag.severity === 'high' ? 'bad' : 'warn'} />
        <span className="font-medium">{flag.flag.replace(/_/g, ' ')}</span>
        {partnerName ? (
          <span className="text-sm text-[color:var(--color-muted)]">
            {flag.affiliateId ? (
              <Link to={`/admin/partners/${flag.affiliateId}`} className="hover:underline">
                {partnerName}
              </Link>
            ) : (
              partnerName
            )}
          </span>
        ) : null}
        {/* Referral flags mostly carry no partner — their subject is the
            company, and it must be reachable from the card being resolved. */}
        {flag.companyId ? (
          <span className="text-sm text-[color:var(--color-muted)]">
            <Link to={`/admin/companies/${flag.companyId}`} className="hover:underline">
              company {flag.companyId}
            </Link>
          </span>
        ) : null}
        <span className="ml-auto text-xs text-[color:var(--color-muted)]">{shortDate(flag.createdAt)}</span>
      </div>
      {flag.detail ? <p className="mt-2 text-sm">{flag.detail}</p> : null}
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <TextInput
            label="Resolution"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="What you looked at and why this is settled"
          />
        </div>
        <div className="pb-4">
          <Button busy={busy} disabled={!reason.trim()} onClick={() => void resolve()}>
            Resolve
          </Button>
        </div>
      </div>
    </div>
  );
}
